/**
 * A PART-FILLED WIZARD THAT SURVIVES THE BROWSER.
 *
 * ── THE DEFECT ──
 * Both intake wizards keep their entire state in a plain `useState` inside a
 * hook mounted by AppRouter. Nothing persists it. So a refresh, a tab restore,
 * an OS reload of a backgrounded tab, or a crash empties it — and because the
 * route guard asks "has a type been chosen", the reader is then bounced back to
 * step 1 with no explanation and no way to recover what they typed.
 *
 * The concierge is interrupted constantly; that IS the job. Any interruption
 * that touches the browser cost them the entry.
 *
 * ── WHY sessionStorage, NOT localStorage ──
 * A half-finished visitor entry is not a document, it is the current
 * conversation at a desk. It should outlive a reload and it must NOT outlive
 * the tab: a front desk is a shared machine, and yesterday's abandoned entry
 * silently reappearing under today's concierge — with a real person's name in
 * it — is worse than losing it. sessionStorage is exactly that lifetime.
 *
 * ── WHY IT IS KEYED, AND WHY THE KEY INCLUDES THE USER ──
 * One key per flow (`visitor` / `activity`) and per signed-in user. Two flows
 * can be part-filled at once, and the same browser can change hands between
 * shifts; restoring one person's draft into another person's session would put
 * a name they never typed into a log entry under their own name.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ──
 * No server draft, no cross-device sync, no conflict resolution. The backend
 * has an onboarding draft endpoint for a different flow; borrowing it here
 * would mean a round trip on every keystroke of a form whose whole value is
 * being instant at a desk.
 */
import { useEffect, useRef } from 'react';

const PREFIX = 'lobbero.wizard';

/** Older shapes are ignored rather than migrated — see restore(). */
const VERSION = 1;

interface Envelope<T> {
  v: number;
  savedAt: number;
  state: T;
}

/**
 * How long a restored draft is still the CURRENT conversation.
 *
 * Twelve hours covers the longest shift with room to spare, and refuses a draft
 * older than the shift that created it. A desk returning on Monday to something
 * typed on Friday would have no idea where it came from.
 */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

const keyFor = (flow: string, userId?: string | number | null): string =>
  `${PREFIX}.${flow}.${userId ?? 'anon'}`;

/** Read a saved draft, or null. Never throws: a corrupt or foreign value is
 *  simply not a draft. */
export const restoreWizardState = <T,>(
  flow: string,
  userId?: string | number | null
): T | null => {
  try {
    const raw = window.sessionStorage.getItem(keyFor(flow, userId));

    if (!raw) return null;

    const parsed = JSON.parse(raw) as Envelope<T>;

    if (!parsed || parsed.v !== VERSION) return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) return null;

    return parsed.state ?? null;
  } catch {
    // Private mode, a quota error, a half-written value — none of them is a
    // reason to break the desk. No draft is the safe answer.
    return null;
  }
};

export const clearWizardState = (
  flow: string,
  userId?: string | number | null
): void => {
  try {
    window.sessionStorage.removeItem(keyFor(flow, userId));
  } catch {
    /* as above */
  }
};

/**
 * Persist `state` for `flow` whenever it changes, and drop it when `isEmpty`.
 *
 * `isEmpty` is the caller's judgement because "nothing typed yet" is
 * flow-specific — the visitor wizard starts with one blank name in an array,
 * which is not the same as an empty form. Writing a draft for an untouched form
 * would resurrect a blank wizard on every reload and mask the real thing.
 */
export const useWizardPersistence = <T,>(
  flow: string,
  state: T,
  isEmpty: boolean,
  userId?: string | number | null
): void => {
  // The first render after a restore writes the value it just read. Harmless,
  // and cheaper than threading a "did we just restore" flag through the caller.
  const key = keyFor(flow, userId);
  const lastWritten = useRef<string | null>(null);

  useEffect(() => {
    try {
      if (isEmpty) {
        window.sessionStorage.removeItem(key);
        lastWritten.current = null;

        return;
      }

      const payload = JSON.stringify({
        v: VERSION,
        savedAt: Date.now(),
        state,
      } as Envelope<T>);

      // Skip an identical write: this effect runs on every keystroke and
      // sessionStorage is synchronous.
      if (payload === lastWritten.current) return;

      window.sessionStorage.setItem(key, payload);
      lastWritten.current = payload;
    } catch {
      /* as above — never break the desk over a draft */
    }
  }, [key, state, isEmpty]);
};
